/* ---------------------- Raw API Shapes ---------------------- */
// Exact shapes returned by /api/missionManagerDashboard/getDashboardData

export interface RawAddress {
  _id?: string;
  addressLineOne: string;
  addressLineTwo: string;
  cityOrTown: string;
  governingDistrict: string;
  zipCode: string;
  country: string;
}

export interface RawCurrentLocation {
  _id: string;
  address: RawAddress;
}

export interface RawLocationOverride {
  lat: number | null;
  lon: number | null;
  alt: number | null;
  heading: number | null;
}

export interface RawDrone {
  _id: string;
  name: string | null;
  status: string | null;
  model: string | null;
  faaRegistrationNumber: string | null;
  totalFlightTime: number | null; // milliseconds
  chargingCycles: number | null;
  firstActivated: string | null;
}

export interface RawBase {
  _id: string;
  name: string | null;
  status: string | null;
  currentLocation: RawCurrentLocation;
  drones: RawDrone[];
  totalMissions: number | null;
  successfulMissions: number | null;
  longestMissionDuration: number | null; // milliseconds
  shortestMissionDuration: number | null; // milliseconds
  averageMissionDuration: number | null; // milliseconds
  lastMissionLandingDate: string | null;
  totalNightMissions: number | null;
  totalFlightTimeBase: number | null; // milliseconds

  totalNumberOfRaidEvents: number | null;
  weeklyScheduledMissions: number | null;

  lat: number | null;
  lon: number | null;
  alt: number | null;
  locationOverride: RawLocationOverride | null;
  location_override: RawLocationOverride | null; // some APIs return this snake_case field instead of locationOverride
}

export interface RawApiResponse {
  bases: RawBase[];
}

/* ---------------------- Raw Websocket Shapes ---------------------- */

export interface RawDroneWsMessage {
  name: string; // matches RawDrone.name
  base_name: string;
  grounded: boolean;
  battery_charge_state: number; // 0–1 float
  battery: number;
  bms_current: number;
  bms_cycle_count: number;
  battery_raw_current: number;
  lat: number;
  lon: number;
  base_id: number;
  // fields not yet in WS:
  chargingCycles?: number;
  firstActivated?: string;
}

export interface RawBaseWsData {
  name: string; // matches RawBase.name
}

export interface RawBaseWsMessage {
  channel: 'base';
  data: RawBaseWsData;
}

/* ---------------------- System Status Enum + Classification Sets ---------------------- */

export enum SystemStatus {
  OPERATIONAL = 'Operational',
  MAINTENANCE = 'Maintenance',
  GROUNDED = 'Grounded',
  OFFLINE = 'Offline',
}

/** Base string statuses that count as "operational" for SystemStatus derivation. */
export const OPERATIONAL_BASE_STATUSES = new Set([
  'Deployed Operational',
  'Deployed Backup',
  'Deployment Customer Validation',
  'Training and Handoff to Customer',
  'Customer Operation',
  'Periodic System Maintenance / On-Site Inspection',
  'On-site Validation',
]);

/** Base string statuses that count as "maintenance" for SystemStatus derivation. */
export const MAINTENANCE_BASE_STATUSES = new Set([
  'No Status',
  'VP (Validation Provisioning)',
  'Validation',
  'DR (Hardware Deployment Review)',
  'DP (Deployment Provisioning)',
  'PR (Package and Packaging Review)',
  'Outbound Shipping / Storage',
  'On-site Unboxing',
  'On-site System Installation',
  'On-Site Repair',
  'Base Arrival Review',
  'Base-MRU',
]);

/** Base string statuses that count as "grounded" for SystemStatus derivation. */
export const GROUNDED_BASE_STATUSES = new Set([]);

/** Base string statuses that count as "offline" for SystemStatus derivation. */
export const OFFLINE_BASE_STATUSES = new Set(['Retired']);

/** Drone string statuses that count as "operational" for SystemStatus derivation. */
export const OPERATIONAL_DRONE_STATUSES = new Set([
  'Deployed Operational',
  'Deployed Backup',
  'Operational but Needs Maintenance',
  'Dev-Temporarily',
  'Dev-Permanently',
]);

/** Drone string statuses that count as "maintenance" for SystemStatus derivation. */
export const MAINTENANCE_DRONE_STATUSES = new Set([
  'No Status',
  'no-status',
  'In-bound Shipping',
  'HQ Arrival Review',
  'HW Build',
  'HW MRU',
  'SW MRU',
  'VP (validation provisioning)',
  'HW-VR (validation hw review)',
  'VHF (validation hand flights)',
  'VMF (validation mission flights)',
  'DP (deployment provision)',
  'HW-DR',
  'HW-PR',
  'Out-bound Shipping',
  'DCR (customer site arrival checks)',
]);

/** Drone string statuses that count as "grounded" for SystemStatus derivation. */
export const GROUNDED_DRONE_STATUSES = new Set([
  'Grounded (Pending MRU)',
  'Grounded (Pending Triage / Incident Investigation)',
  'Grounded (Pending Field MRU)',
]);

/** Drone string statuses that count as "offline" for SystemStatus derivation. */
export const OFFLINE_DRONE_STATUSES = new Set(['Retired']);

/* ---------------------- Normalized Shapes ---------------------- */

export type DroneModel = 'blockOneFive' | 'blockFive' | 'blockFiveT' | string;

export interface NormalizedDrone {
  id: string;
  name: string | null;
  faaRegistrationNumber: string | null;
  model: DroneModel | null;

  // status
  status: string | null;
  isGrounded: boolean | null;

  // battery
  batteryLevel: number | null; // 0–100 percentage
  chargingCycles: number | null;
  charging: boolean | null;

  // stats
  totalFlightMinutes: number | null;
  firstActivated: string | null;
}

export interface NormalizedBase {
  // identity
  id: string;
  name: string | null;

  // address
  fullAddress: string | null;
  partialAddress: string | null;

  // position
  lat: number | null;
  lon: number | null;

  // status
  systemStatus: SystemStatus; // derived from base + drone statuses
  baseStatus: string | null; // the base's own string status (e.g. "Customer Operation")
  chargingStatus: string | null;

  // mission stats
  totalMissions: number | null;
  successRate: number | null;
  averageMissionDuration: number | null; // minutes
  longestMissionDuration: number | null; // minutes
  shortestMissionDuration: number | null; // minutes
  numberOfNightMissions: number | null;
  numberOfDayMissions: number | null;
  lastMissionLandingDate: string | null;
  totalFlightMinutesBase: number | null; // minutes

  // alerts and scheduling
  totalNumberOfRaidEvents: number | null;
  weeklyScheduledMissions: number | null;

  // drones (dev drones filtered out)
  drones: NormalizedDrone[] | null;
}

/* ---------------------- Misc UI Types ---------------------- */

export enum SortKey {
  MOST_MISSIONS = 'mostMissions',
  AVERAGE_MISSION_DURATION = 'averageMissionDuration',
  NUMBER_OF_NIGHT_MISSIONS = 'nightOps',
  NUMBER_OF_DAY_MISSIONS = 'dayOps',
}

export enum StatusIndicatorContext {
  ASSET_ROSTER = 'ASSET_ROSTER',
  TACTICAL_MAP = 'TACTICAL_MAP',
}

export interface SystemStatusColors {
  core: string;
  background: string;
  bright: string;
}
